import { HydraUIAPIError, type AuthSSOLoginConfiguration } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';

export type SsoIdentityProvider = 'google_oidc' | 'okta_oidc' | 'azure_oidc' | 'generic_oidc' | 'generic_saml';

/**
 * The subset of an account's SSO configuration returned by the unauthenticated
 * `login_sso_info` endpoint — only what the login flow needs to drive the SSO
 * step. (Not the full account-admin SSO configuration record.)
 *
 * Built from the generated response schema rather than restated field by field,
 * so a renamed or dropped field breaks the build here instead of drifting
 * silently. Two deliberate departures from the generated type:
 *   - `identity_provider` is narrowed to the providers this flow switches on; the
 *     schema types it as a bare `string`.
 *   - the provider-specific fields stay optional. The endpoint always sends every
 *     key (`services/ui_api/auth/login_sso_info.go` is plain strings, no
 *     `omitempty`), but only the ones belonging to the configured provider carry a
 *     value, and every call site already treats a blank one as unconfigured.
 */
export type SsoConfiguration = Partial<Omit<AuthSSOLoginConfiguration, 'identity_provider'>> & {
  identity_provider: SsoIdentityProvider;
};

export type SsoInfo = {
  ssoConfiguration: SsoConfiguration | null;
  /** The Google account email on record for this user, used as `login_hint`. */
  userExternalIdentityEmail: string | null;
};

/**
 * Look up the SSO configuration for an email at login time.
 *
 * Hits the unauthenticated `login_sso_info` endpoint. A 404 means the account
 * has no SSO configured (the common case), which resolves to a null
 * configuration rather than throwing.
 */
export async function fetchSsoInfo({ email, addAccount }: { email: string; addAccount: boolean }): Promise<SsoInfo> {
  try {
    const data = await hydraUiApi.auth.getLoginSsoInfo({
      params: { query: { email, add_session: String(addAccount) } },
    });

    return {
      ssoConfiguration: {
        ...data.sso_configuration,
        // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
        identity_provider: data.sso_configuration.identity_provider as SsoIdentityProvider,
      },
      userExternalIdentityEmail: data.user_external_identity_email ?? null,
    };
  } catch (error) {
    if (error instanceof HydraUIAPIError && error.status === 404) {
      return { ssoConfiguration: null, userExternalIdentityEmail: null };
    }
    throw error;
  }
}
